// World / screen camera for widescreen extended FOV (Fase 2).

import {
  BASE_SCREEN_W,
  BASE_SCREEN_H,
  SCREEN_W,
  SCREEN_H,
  TILE,
  RES_SCALE,
} from '../core/constants';
import { maxVisibleTilesX } from './render-config';
import { isCoarsePointer } from '../ui/device';

const tr = Math.trunc;

/** Legacy 4:3 tile grid (DrawLevel parity at 1x: 21×16). */
export const LEGACY_VISIBLE_TILES_X = 21;
export const LEGACY_VISIBLE_TILES_Y = 16;
/** Target on-screen tile size in CSS px. 58 ≈ 2× the ~30 px typical on phones. */
export const MOBILE_TILE_SCREEN_PX = 58;
export const MOBILE_MIN_TILES_X = 8;
export const MOBILE_MIN_TILES_Y = 6;
/** Extra tile rings drawn beyond visible FOV (covers scroll gaps). */
export const TILE_DRAW_MARGIN = 2;

export interface Camera {
  /** World scroll position (game pixels, same as G.scroll_x/y). */
  scrollX: number;
  scrollY: number;
  /** Tiles drawn horizontally / vertically. */
  visibleTilesX: number;
  visibleTilesY: number;
  /** Game-pixel width / height of the render viewport. */
  viewW: number;
  viewH: number;
  /** Game pixels per screen pixel (inverse of display scale). */
  pixelScale: number;
  /** Sub-pixel tile scroll within the left/top edge tile. */
  tileOffX: number;
  tileOffY: number;
  /** First world tile column / row index at the left/top of the view. */
  baseGX: number;
  baseGY: number;
  /** Horizontal padding when viewW > legacy SCREEN_W (extra FOV split evenly). */
  padX: number;
  /** Screen-pixel crop inset when cover scale exceeds viewport (mobile). */
  coverCropX: number;
  coverCropY: number;
}

export interface ViewportSize {
  w: number;
  h: number;
}

/**
 * Compute camera from viewport aspect and scroll.
 * Extended landscape FOV shows more tiles horizontally; vertical stays at 16.
 */
export function computeCamera(
  viewport: ViewportSize,
  scrollX: number,
  scrollY: number
): Camera {
  const aspect = viewport.w / Math.max(1, viewport.h);
  const baseAspect = SCREEN_W / SCREEN_H;

  let visibleTilesX: number;
  let visibleTilesY: number;
  let viewH: number;
  let viewW: number;
  let pixelScale: number;
  let coverCropX = 0;
  let coverCropY = 0;

  if (isCoarsePointer()) {
    const targetScale = MOBILE_TILE_SCREEN_PX / TILE;
    visibleTilesY = Math.max(
      MOBILE_MIN_TILES_Y,
      Math.floor(viewport.h / (TILE * targetScale))
    );
    visibleTilesX = Math.max(
      MOBILE_MIN_TILES_X,
      Math.ceil(viewport.w / (TILE * targetScale))
    );
    viewH = visibleTilesY * TILE;
    viewW = visibleTilesX * TILE;
    pixelScale = Math.max(viewport.w / viewW, viewport.h / viewH);
    const scaledW = viewW * pixelScale;
    const scaledH = viewH * pixelScale;
    coverCropX = Math.max(0, (scaledW - viewport.w) / 2);
    coverCropY = Math.max(0, (scaledH - viewport.h) / 2);
  } else {
    visibleTilesX = Math.max(
      LEGACY_VISIBLE_TILES_X,
      Math.ceil(LEGACY_VISIBLE_TILES_X * (aspect / baseAspect))
    );
    const cap = maxVisibleTilesX();
    if (cap !== null) visibleTilesX = Math.min(visibleTilesX, cap);
    visibleTilesY = LEGACY_VISIBLE_TILES_Y;
    viewH = SCREEN_H;
    viewW = visibleTilesX * TILE;
    pixelScale = Math.min(viewport.w / viewW, viewport.h / viewH);
  }

  const tileOffX = ((scrollX % TILE) + TILE) % TILE;
  const tileOffY = ((scrollY % TILE) + TILE) % TILE;
  const baseGX = tr(scrollX / TILE);
  const baseGY = tr(scrollY / TILE);

  return {
    scrollX,
    scrollY,
    visibleTilesX,
    visibleTilesY,
    viewW,
    viewH,
    pixelScale,
    tileOffX,
    tileOffY,
    baseGX,
    baseGY,
    padX: 0,
    coverCropX,
    coverCropY,
  };
}

/** Scroll target to centre the player in the current view. */
export function scrollForPlayerCenter(
  playerX: number,
  playerY: number,
  playerW: number,
  playerH: number,
  cam: Pick<Camera, 'viewW' | 'viewH'>
): { x: number; y: number } {
  return {
    x: playerX + tr(playerW / 2) - Math.round(cam.viewW / 2),
    y: playerY + tr(playerH / 2) - Math.round(cam.viewH / 2),
  };
}

/** World position → game-screen pixel (relative to view origin). */
export function worldToScreen(
  wx: number,
  wy: number,
  cam: Camera
): { x: number; y: number } {
  return {
    x: wx - cam.scrollX,
    y: wy - cam.scrollY,
  };
}

/** Game-screen pixel → world position. */
export function screenToWorld(
  sx: number,
  sy: number,
  cam: Camera
): { x: number; y: number } {
  return {
    x: sx + cam.scrollX,
    y: sy + cam.scrollY,
  };
}

/** Map viewport client coords to game pixels using camera layout. */
export function viewportToGameCamera(
  clientX: number,
  clientY: number,
  canvasRect: { left: number; top: number },
  cam: Camera
): { x: number; y: number } {
  const scale = cam.pixelScale;
  const localX = (clientX - canvasRect.left) / scale;
  const localY = (clientY - canvasRect.top) / scale;
  return { x: localX, y: localY };
}

/** Logical world units per legacy constant (for docs / tests). */
export const WORLD_TILE_PX = TILE;
export const LEGACY_WORLD_W = BASE_SCREEN_W * RES_SCALE;
export const LEGACY_WORLD_H = BASE_SCREEN_H * RES_SCALE;
